/**
 * src/pages/auth/OAuthCallback.tsx — 2304vc
 *
 * Shared OAuth callback handler. Receives ?code= from provider,
 * calls the oauth-token-exchange edge function, then redirects
 * back to the venue inflow page.
 *
 * Each provider gets a thin wrapper that passes the right provider name.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

interface OAuthCallbackProps {
  provider: 'onedrive' | 'gdrive' | 'dropbox'
}

function OAuthCallback({ provider }: OAuthCallbackProps) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'exchanging' | 'success' | 'error'>('exchanging')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const code     = searchParams.get('code')
    const error    = searchParams.get('error')
    const venueId  = sessionStorage.getItem('oauth_venue_id')
    const venueSlug = sessionStorage.getItem('oauth_venue_slug')

    if (error) {
      setError(`Provider declined: ${error}`)
      setStatus('error')
      return
    }

    if (!code) {
      setError('No authorization code received')
      setStatus('error')
      return
    }

    if (!venueId) {
      setError('Session expired — please try connecting again')
      setStatus('error')
      return
    }

    const redirectUri = `${window.location.origin}/auth/${provider}/callback`

    const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL
    const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY

    fetch(`${SUPABASE_URL}/functions/v1/oauth-token-exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON,
        'Authorization': `Bearer ${SUPABASE_ANON}`,
      },
      body: JSON.stringify({ provider, code, venue_id: venueId, redirect_uri: redirectUri })
    }).then(async res => {
      const data = await res.json()
      console.log('OAuth response:', res.status, JSON.stringify(data))
      if (!res.ok || !data.success) {
        setError(`${res.status}: ${data.error ?? JSON.stringify(data)}`)
        setStatus('error')
        return
      }
      sessionStorage.setItem('oauth_connected_source_id', data.source_id)
      sessionStorage.setItem('oauth_connected_provider', provider)
      setStatus('success')
      setTimeout(() => {
        navigate(`/venue/${venueSlug}/inflow`, { replace: true })
      }, 1200)
    }).catch(err => {
      setError(err.message)
      setStatus('error')
    })
  }, [])

  const providerLabel =
    provider === 'onedrive' ? 'OneDrive' :
    provider === 'gdrive'   ? 'Google Drive' :
                              'Dropbox'

  const providerColor =
    provider === 'onedrive' ? '#0078D4' :
    provider === 'gdrive'   ? '#4285F4' :
                              '#0061FF'

  return (
    <div className="fixed inset-0 bg-black flex flex-col items-center justify-center px-6 gap-6 text-center">
      {/* Provider icon placeholder */}
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
        style={{ backgroundColor: `${providerColor}20`, border: `1px solid ${providerColor}40` }}>
        <span className="text-3xl">
          {provider === 'onedrive' ? '☁' : provider === 'gdrive' ? '🗂' : '📦'}
        </span>
      </div>

      {status === 'exchanging' && (
        <>
          <div>
            <p className="text-white font-semibold text-lg">Connecting {providerLabel}</p>
            <p className="text-white/40 text-sm mt-1">Securing your credentials…</p>
          </div>
          <div className="flex gap-1.5">
            {[0,1,2].map(i => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-white/40 animate-pulse"
                style={{ animationDelay: `${i * 150}ms` }} />
            ))}
          </div>
        </>
      )}

      {status === 'success' && (
        <div>
          <p className="text-white font-semibold text-lg">✓ {providerLabel} connected</p>
          <p className="text-white/40 text-sm mt-1">Returning to inflow…</p>
        </div>
      )}

      {status === 'error' && (
        <div>
          <p className="text-white font-semibold text-lg">Connection failed</p>
          <p className="text-white/40 text-sm mt-2 max-w-xs break-all">{error}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 rounded-xl bg-white/10 text-white text-sm hover:bg-white/15 transition-colors"
          >
            Go back
          </button>
        </div>
      )}
    </div>
  )
}

export function OneDriveCallback()  { return <OAuthCallback provider="onedrive" /> }
export function GDriveCallback()    { return <OAuthCallback provider="gdrive"   /> }
export function DropboxCallback()   { return <OAuthCallback provider="dropbox"  /> }
